/**
 * A panel hung off a button in the bar. Deliberately not `role="menu"`: what hangs here
 * is links and a segmented control, not menu items, so the plain expanded-or-collapsed
 * disclosure is what a screen reader should hear and arrow keys are not promised.
 *
 * It closes on Escape, on a click outside, and when focus leaves it altogether, which is
 * what tabbing off the last item does.
 */
import { useEffect, useId, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

export function Menu({ label, triggerClass, trigger, children }: {
  label: string;
  triggerClass?: string;
  trigger: ComponentChildren;
  children: ComponentChildren;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    const onDown = (e: Event) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    addEventListener("keydown", onKey, true);
    addEventListener("mousedown", onDown, true);
    return () => { removeEventListener("keydown", onKey, true); removeEventListener("mousedown", onDown, true); };
  }, [open]);

  return (
    <div
      class="menu-wrap"
      ref={wrap}
      onFocusOut={(e) => { if (open && !wrap.current?.contains(e.relatedTarget as Node | null)) setOpen(false); }}
    >
      <button
        ref={button} type="button" class={triggerClass ?? ""} aria-label={label}
        aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      <div class="menu" id={id} hidden={!open}>{children}</div>
    </div>
  );
}
